const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const dataAccessMethods = require('../../../../dataAccessMethods');

const TOOLTIP = 'A comma separated list of values to include or exclude for the property name.';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    blocklyBlock.setInputsInline(true);
    const blockId = blocklyBlock.id;
    const workspace = blocklyBlock.workspace;

    line1OfInputs
        .appendField('  Optional Namespace:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getNamespaces(true)), 'namespace')
        .appendField('  Optional Relation:')
        .appendField(
            new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
            'relation'
        )
        .appendField('  Property Name:')
        .appendField(new Blockly.FieldTextInput('title'), 'propertyName')
        .appendField('  Negative (false) Option:')
        .appendField(new Blockly.FieldTextInput(''), 'toggleOption1')
        .appendField('  Positive (true) Option:')
        .appendField(new Blockly.FieldTextInput('true'), 'toggleOption2')
        .appendField('  Values:')
        .appendField(new Blockly.FieldTextInput('', validateCommaSeparatedList), 'values')
        .appendField('  Value Exists:')
        .appendField(new Blockly.FieldCheckbox('FALSE'), 'valueExists')
        .appendField('  Include Undefined:')
        .appendField(new Blockly.FieldCheckbox('FALSE'), 'includeUndefined');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    const propertyName = blocklyBlock.getFieldValue('propertyName');
    const opt1 = blocklyBlock.getFieldValue('toggleOption1');
    const opt2 = blocklyBlock.getFieldValue('toggleOption2');
    const parent = blocklyBlock.getSurroundParent();
    const parentChildren = parent ? parent.getDescendants(undefined) : [];
    addCodeProperties([
        'namespace',
        'relation',
        { key: 'propertyName', handler: () => propertyName },
        { key: 'values', handler: val => val.split(',').map(s => s.trim()) },
        { key: 'valueExists', handler: () => blocklyBlock.getFieldValue('valueExists') === 'TRUE' },
        { key: 'includeUndefined', handler: () => blocklyBlock.getFieldValue('includeUndefined') === 'TRUE' },
        { key: 'toggleValues', handler: () => [opt1, opt2] },
        { key: 'forAction', handler: () => 'filter' },
        {
            key: 'sequence',
            handler: () =>
                parentChildren.findIndex(function (value) {
                    return value.id === blocklyBlock.id;
                }) - 1 // subtract 1 because includes parent with getDescendants
        }
    ]);
    return code;
}

/** Builds the data dictionary fragment for the IncludedValueSearch search element. */
function asDataDictionary(hNode, context) {
    const { id, title, namespace, relation, propertyName: _propertyPath, toggleValues, valueExists, includeUndefined } = hNode;
    let propertyPath = _propertyPath.split('.');
    const propertyName = propertyPath.pop(); //pop() removes and returns the last element

    // Create TypeBox string schema with search-specific metadata
    const stringSchema = Type.String({
        'x-id': id,
        title: title,
        'x-dataType': 'string',
        'x-propertyName': propertyName,
        'x-forAction': 'filter',
        'x-toggleValues': toggleValues,
        'x-valueExists': valueExists,
        'x-includeUndefined': includeUndefined
    });

    let partialTypeDefinition = {
        [propertyName]: stringSchema
    };

    // If property path has nested parts (e.g., 'nested.path.property'), wrap in TypeBox objects
    if (propertyPath.length) {
        // reduceRight is a reduce function that starts from the end of the array and works its way to the beginning
        partialTypeDefinition = propertyPath.reduceRight((acc, path) => {
            return {
                [path]: Type.Object(
                    acc,
                    {
                        'x-dataType': 'object',
                        'x-propertyName': path,
                        additionalProperties: true
                    }
                )
            };
        }, partialTypeDefinition);
    }

    let subDataDictionary = {};
    if (namespace && relation) {
        // Register foreign key to declare the relation and get proper TypeBox schema
        const properties = [propertyName];
        partialTypeDefinition = context.registerForeignKey(namespace, relation, properties, { id, title });
        Object.values(partialTypeDefinition || {}).forEach(schema => {
            if (schema && typeof schema === 'object') {
                // Preserve the filter action context for downstream required/visibility logic
                schema['x-forAction'] = 'filter';
            }
        });
    }

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };

/**
 * If the input matches return the input otherwise return null
 * @param {string} text This should be a comma separated list
 */
function validateCommaSeparatedList(text) {
    if (text === '') return text;
    // Regex explanation:
    // ^[- \w]+         : Start with one or more word characters, spaces, or hyphens
    // (?:,[- \w]*)*   : Followed by zero or more occurrences of a comma and then zero or more word characters, spaces, or hyphens
    // $               : End of the string
    const match = /^[- \w]+(?:,[- \w]*)*$/.exec(text);
    if (match && match.length > 0) {
        return text;
    }
    return null;
}
